import React from 'react';
import { useLingui } from '@lingui/react/macro';

import Close from '@rover/icons/svg/close.svg';
import { Box, Button, Flex, Heading, Paragraph } from '@rover/kibble/core';
import useScrollRegionFocus from '@rover/kibble/hooks/useScrollRegionFocus';

import { type DialogButtonProps, type ModalCommonProps, DIALOG_START_PROPS } from '../Modal.common';

import DialogButtons, { hasDialogButtons } from './DialogButtons';
import TitleFocusTarget from './TitleFocusTarget';

export type Props = DialogButtonProps &
  Omit<
    ModalCommonProps,
    | 'isOpen'
    | 'isDismissible'
    | 'shouldCloseOnOverlayClick'
    | 'headerText'
    | 'headerSize'
    | 'headerAlign'
    | 'headerProps'
    | 'maxHeight'
    | 'appElementSelector'
  > & {
    headerId?: string;
    contentWrapperHeight?: string;
    scrollsBody?: boolean;
    /** Native only: the view screen readers start on, the title, label or else the subtitle. */
    titleRef?: React.RefObject<unknown>;
    /** Native only: keeps screen readers off the ✕ until they've started on `titleRef`. */
    hidesCloseIcon?: boolean;
  };

export default function ModalContent({
  children,
  showCloseIcon,
  closeIconSize = 'default',
  title,
  headerId,
  titleSize = '300',
  titleAlign = 'left',
  titleProps = {},
  titleRef,
  hidesCloseIcon = false,
  accessibilityLabel,
  subtitle,
  customFooter,
  contentWrapperHeight,
  scrollsBody = false,
  onRequestClose,
  ...buttonProps
}: Props): JSX.Element {
  const { t } = useLingui();
  const scrollRegion = useScrollRegionFocus(title ? headerId : undefined, scrollsBody);
  // Web names the dialog with the label instead.
  const startsOnLabel = Boolean(titleRef && !title && accessibilityLabel);
  const startsOnSubtitle = !title && !startsOnLabel && Boolean(subtitle);
  // VoiceOver can't reliably focus a 1px view, so the heading spans the dialog's width. It reads
  // in screen order, so the heading starts below the ✕ to keep the ✕ first.
  const labelHeading = startsOnLabel && (
    <Box
      ref={titleRef as React.RefObject<never>}
      position="absolute"
      top={showCloseIcon ? '100%' : '0'}
      left="0"
      right="0"
      height="44px"
      sx={{ pointerEvents: 'none' }}
      rxnProps={{ accessible: true, accessibilityRole: 'header', accessibilityLabel }}
    />
  );
  const startsOnBody = !title && !subtitle;
  const subtitleText = (
    <Paragraph
      size="200"
      mt="0x"
      mb="4x"
      {...(startsOnSubtitle && {
        webProps: DIALOG_START_PROPS,
        sx: { ':focus': { outline: 'none' } },
      })}
    >
      {subtitle}
    </Paragraph>
  );

  return (
    <Flex
      flexDirection="column"
      width="100%"
      height={contentWrapperHeight}
      {...(scrollsBody && { flexShrink: '1', webProps: { minHeight: '0px' } })}
    >
      {(title || showCloseIcon) && (
        <Flex
          flexDirection="row"
          justifyContent={title ? 'space-between' : 'flex-end'}
          alignItems="center"
          mb={subtitle ? '2x' : '4x'}
          {...titleProps}
        >
          {title && (
            <TitleFocusTarget titleRef={titleRef} fill={titleAlign === 'center'}>
              <Heading
                id={headerId}
                size={titleSize}
                m="0x"
                {...(titleAlign === 'center' && { sx: { flex: 1, textAlign: 'center' } })}
                {...(titleRef && { rxnProps: { accessibilityRole: 'none' } })}
              >
                {title}
              </Heading>
            </TitleFocusTarget>
          )}
          {showCloseIcon && (
            <Box
              ml="4x"
              {...(hidesCloseIcon && {
                rxnProps: {
                  importantForAccessibility: 'no-hide-descendants',
                  accessibilityElementsHidden: true,
                },
              })}
            >
              <Button
                icon={Close}
                variant="flat"
                onClick={onRequestClose}
                size={closeIconSize}
                data-qa-id="modal-dismiss-button"
                data-testid="modal-dismiss-button"
                aria-label={t`Close`}
                sx={{ color: 'text.primary', '&:hover, &:focus': { color: 'text.primary' } }}
                circular
              />
            </Box>
          )}
          {showCloseIcon && labelHeading}
        </Flex>
      )}
      {!showCloseIcon && labelHeading}
      {subtitle &&
        (startsOnSubtitle && titleRef ? (
          // TalkBack can only focus an accessible view.
          <Box ref={titleRef as React.RefObject<never>} rxnProps={{ accessible: true }}>
            {subtitleText}
          </Box>
        ) : (
          subtitleText
        ))}
      <Box
        ref={scrollRegion.ref}
        flex="1"
        flexBasis="auto"
        pt="0x"
        // A scrolling body clips focus rings at its edges, so it pads 8px and pulls 8px into the
        // modal's padding, leaving the layout unchanged.
        {...((scrollsBody || startsOnBody) && {
          webProps: {
            ...(startsOnBody && DIALOG_START_PROPS),
            ...scrollRegion.focusProps,
            ...(scrollsBody && { minHeight: '0px' }),
            sx: {
              ...(scrollsBody && {
                overflowY: 'auto',
                m: '-2x',
                p: '2x',
                '&:focus-visible': { outlineOffset: '-2px' },
              }),
              '&[tabindex="-1"]:focus': { outline: 'none' },
            },
          },
        })}
      >
        {children}
      </Box>
      <DialogButtons {...buttonProps} />
      {customFooter && <Box mt={hasDialogButtons(buttonProps) ? '4x' : '8x'}>{customFooter}</Box>}
    </Flex>
  );
}
